import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, imgUrl } from "@/api";
import Lightbox from "@/components/Lightbox";
import { FadeUp } from "@/components/Reveal";

const TABS = [
  { id: "all", label: "All Frames" },
  { id: "automotive", label: "Automotive" },
  { id: "personal", label: "Personal / Portrait" },
];

export default function Gallery() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(null);

  useEffect(() => {
    api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (cat === "all" ? photos : photos.filter((p) => p.category === cat)),
    [photos, cat]
  );
  const active = activeIdx !== null ? filtered[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (activeIdx === null || filtered.length === 0) return;
      if (e.key === "Escape") setActiveIdx(null);
      if (e.key === "ArrowRight") setActiveIdx((i) => (i + 1) % filtered.length);
      if (e.key === "ArrowLeft") setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIdx, filtered.length]);

  return (
    <main className="min-h-screen" data-testid="gallery-page">
      <section className="px-4 sm:px-8 lg:px-16 pt-14 lg:pt-20 pb-10">
        <FadeUp>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-3">The work</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone">Every frame, labeled</h1>
          <p className="mt-4 max-w-lg text-sm text-ash leading-relaxed">
            Open any frame for the full story — and book the same session style straight from the lightbox.
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2" role="tablist">
            {TABS.map((t) => {
              const count = t.id === "all" ? photos.length : photos.filter((p) => p.category === t.id).length;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={cat === t.id}
                  data-testid={`filter-tab-${t.id}`}
                  onClick={() => { setParams(t.id === "all" ? {} : { cat: t.id }); setActiveIdx(null); }}
                  className={`px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
                    cat === t.id
                      ? "border-flame bg-flame text-ink"
                      : "border-white/15 text-ash hover:border-flame/60 hover:text-bone"
                  }`}
                >
                  {t.label} <span className="opacity-60">({count})</span>
                </button>
              );
            })}
          </div>
        </FadeUp>
      </section>

      <section className="px-4 sm:px-8 lg:px-16 pb-24">
        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-faint" data-testid="gallery-loading">Loading frames…</p>
        ) : (
          <div className="columns-1 sm:columns-2 xl:columns-3 gap-5" data-testid="gallery-grid">
            {filtered.map((p, i) => (
              <motion.button
                key={p.id}
                data-testid={`photo-card-${p.id}`}
                onClick={() => setActiveIdx(i)}
                className="group relative block w-full mb-5 break-inside-avoid text-left border border-white/10 overflow-hidden bg-coal"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={imgUrl(p.thumb)}
                    alt={p.title}
                    loading="lazy"
                    className="w-full transition-transform duration-700 group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: "radial-gradient(400px at 50% 40%, rgba(212,74,58,0.18), transparent 70%)" }}
                  />
                </div>
                <div className="flex items-start justify-between gap-3 p-4">
                  <div>
                    <p className="font-serif text-lg text-bone leading-snug">{p.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-faint mt-1">
                      {p.subcategory} — {p.specs}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 border ${
                      p.category === "automotive" ? "border-flame/50 text-flame" : "border-copper/50 text-copper"
                    }`}
                  >
                    {p.category === "automotive" ? "Auto" : "Portrait"}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {active && (
          <Lightbox
            photo={active}
            onClose={() => setActiveIdx(null)}
            onPrev={() => setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length)}
            onNext={() => setActiveIdx((i) => (i + 1) % filtered.length)}
          />
        )}
      </AnimatePresence>
    </main>
  );
}
